import { useMemo } from 'react';
import {
  collapseSingleChildLeaves,
  NestedOptions,
  buildDescendantMap,
  sortNestedOptionsTree,
  extractValueLabels,
} from '../TreeSelect.helpers';
import { useNestedKeyComparator } from '../useNestedKeyComparator';
import { SelectItem } from '@/types/ui';
import type { NestedKeySort } from '../TreeSelect.types';

type UseTreeSelectOptionsParams<T extends string> = {
  options: SelectItem<T>[];
  value: T[];
  collapseSingleChild?: boolean;
  selectionMode: 'standard' | 'indication';
  nestedKeySort: NestedKeySort;
};

export function useTreeSelectOptions<T extends string>({
  options,
  value,
  collapseSingleChild,
  selectionMode,
  nestedKeySort,
}: UseTreeSelectOptionsParams<T>) {
  const fixedOptions = useMemo(() => {
    const purgedOptions = options.filter(opt => !!opt.value);
    return collapseSingleChild ? collapseSingleChildLeaves(purgedOptions) : purgedOptions;
  }, [collapseSingleChild, options]);

  // Cascading filters: paths whose value returns no data for the current filter
  // combination. A branch is enabled while any descendant is available, so only
  // paths explicitly flagged as disabled (and with no enabled descendant) count.
  const disabledPathSet = useMemo(() => {
    const enabled = new Set<string>();
    for (const option of fixedOptions) {
      if (option.disabled !== true && typeof option.value === 'string') {
        const segments = option.value.split('__');
        let prefix = '';
        for (const segment of segments) {
          prefix = prefix ? `${prefix}__${segment}` : segment;
          enabled.add(prefix);
        }
      }
    }
    const disabled = new Set<string>();
    for (const option of fixedOptions) {
      if (typeof option.value === 'string' && !enabled.has(option.value)) {
        disabled.add(option.value);
      }
    }
    return disabled;
  }, [fixedOptions]);

  const descendantMap = useMemo(() => buildDescendantMap(fixedOptions, selectionMode), [fixedOptions, selectionMode]);

  // Structural tree — depends only on fixedOptions, not value.
  // Checked state is derived at render time from selectedSet, so toggling
  // a checkbox no longer triggers an O(N log N) tree rebuild + re-sort.
  const nestedOptions = useMemo((): NestedOptions => {
    const nestedObject: NestedOptions = {};

    for (const option of fixedOptions) {
      const parts = option.value.split('__');
      let currentLevel = nestedObject;
      let currentPath = '';

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        currentPath = i === 0 ? part : `${currentPath}__${part}`;

        if (!currentLevel[part]) {
          currentLevel[part] = { path: currentPath, subOptions: {}, checked: false };
        }

        currentLevel = currentLevel[part].subOptions;
      }
    }

    return nestedObject;
  }, [fixedOptions]);

  const compareNestedKeys = useNestedKeyComparator(nestedKeySort);

  const sortedNestedOptions = useMemo(
    () => sortNestedOptionsTree(nestedOptions, compareNestedKeys),
    [nestedOptions, compareNestedKeys],
  );

  const valueLabelList = useMemo(() => extractValueLabels(value), [value]);

  return {
    fixedOptions,
    descendantMap,
    disabledPathSet,
    nestedOptions,
    sortedNestedOptions,
    valueLabelList,
  };
}
